import { call, put, select, takeLatest } from 'redux-saga/effects';
import { onIdTokenChanged } from 'firebase/auth';

import { types, actions } from '../auth/refresh';
import * as tokensModule from '../auth/tokens';

import { firebaseAuth } from '../../../../src/firebase.js';

// Firebase restores its signed-in user asynchronously on page load, so
// firebaseAuth.currentUser is null for a moment even for a logged-in visitor.
// This resolves once Firebase has reported its first auth state (runs once, cached).
let firebaseReadyPromise = null;
const waitForFirebaseReady = () => {
  if (!firebaseReadyPromise) {
    firebaseReadyPromise = new Promise(resolve => {
      let unsubscribe = null;
      unsubscribe = onIdTokenChanged(firebaseAuth, () => {
        if (unsubscribe) unsubscribe();
        resolve();
      });
    });
  }
  return firebaseReadyPromise;
};

// Refreshes a Firebase (customer) session's ID token and stores it as the access
// token. A refresh is NOT a new login, so this deliberately bypasses loginSaga
// (whose replaceExistingSession would clear tokens + cb_auth and reset all data).
export function* refreshFirebaseTokenSaga() {
  const { access, refresh } = yield select(state => state.auth.tokens);

  // Business (email/password) sessions have a refresh token and are handled by
  // the generic refresh saga.
  if (refresh || !access) return;

  try {
    yield put(actions.refreshAuthRequest());
    yield call(waitForFirebaseReady);

    const firebaseUser = firebaseAuth.currentUser;
    if (!firebaseUser) {
      throw new Error('Firebase session expired.');
    }

    const firebaseIdToken = yield call([firebaseUser, 'getIdToken'], true);
    const tokens = { access: firebaseIdToken, refresh: null };

    yield put(tokensModule.actions.setTokens(tokens));
    yield put(actions.refreshAuthSuccess(tokens));
  } catch (error) {
    yield put(actions.refreshAuthFailure(error.message));
  }
}

export default function* saga() {
  yield takeLatest(types.REFRESH_AUTH, refreshFirebaseTokenSaga);
}